import { useQuery } from "@apollo/client";
import AddLocationAltOutlined from "@mui/icons-material/AddLocationAltOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, Alert, Link as MuiLink } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { TruncateDataGridCell } from "components/TruncateDataGridCell";
import CopyableTypography from "components/Typography/CopyableTypography";
import { useSubmissionFormContext } from "contexts/SubmissionFormContext";
import { useTenant } from "hooks/useTenant";
import { SUBMISSION_FACILITIES_QUERY } from "queries/submissions";
import React, { useEffect, useMemo, useState } from "react";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";

import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

interface SubmissionFacilitiesDatagridProps {
  facilityIds: string[];
  onRemoveFacility?: (facilityId: string) => void;
  onAddFacility?: () => void;
}

type FacilityRow = {
  id: string;
  name: string;
  customerFacilityId?: string | null;
  streetAddress1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  epaId?: string | null;
};

export function SubmissionFacilitiesDatagrid({
  facilityIds,
  onRemoveFacility,
  onAddFacility,
}: SubmissionFacilitiesDatagridProps) {
  // We're caching these mostly because the associate facilities dialog is a
  // paginated datagrid and we will only have the ids of certain selected facilities
  // otherwise it would be better to pass the entire facilities back to this component
  const [cachedFacilities, setCachedFacilities] = useState<Map<string, FacilityRow>>(
    new Map<string, FacilityRow>()
  );
  const { issues } = useSubmissionFormContext();
  const facilityIssues = useMemo(
    () => issues.filter((i) => i.key === "facilitySubmissions"),
    [issues]
  );

  const { tenantId } = useTenant();

  const rows: FacilityRow[] = useMemo(() => {
    return facilityIds.map((id) => cachedFacilities.get(id)).filter((f) => !!f) ?? [];
  }, [cachedFacilities, facilityIds]);

  const toFetchFacilityIds = useMemo(() => {
    return facilityIds.filter((id) => !cachedFacilities.has(id));
  }, [cachedFacilities, facilityIds]);

  // Create search query to filter by facility IDs
  const searchQuery = useMemo(() => {
    if (toFetchFacilityIds.length === 0) return "";
    return `ids:${toFetchFacilityIds.join(",")}${tenantId ? ` tenantId:${tenantId}` : ""}`;
  }, [toFetchFacilityIds, tenantId]);

  const { data: facilities, loading } = useQuery(SUBMISSION_FACILITIES_QUERY, {
    variables: {
      search: searchQuery,
    },
    skip: toFetchFacilityIds.length === 0,
  });

  useEffect(() => {
    if (facilities?.facilities?.items) {
      setCachedFacilities((prev) => {
        const newCachedFacilities = new Map(prev);
        facilities.facilities.items.forEach((item) => {
          const epaAlt = item.facilityAlternateIds?.find((a) => a.type === "EPA" && !a.hasNoId);
          newCachedFacilities.set(item.id, {
            ...item,
            epaId: epaAlt?.value ?? null,
          });
        });
        return newCachedFacilities;
      });
    }
  }, [facilities]);

  const getActionsColumn = (): GridColDef<FacilityRow> => ({
    field: "actions",
    headerName: "",
    type: "actions",
    width: 40,
    sortable: false,
    filterable: false,
    renderCell: (params: { row: FacilityRow }) => (
      <IconButton
        size="small"
        onClick={() => onRemoveFacility?.(params.row.id)}
        title="Remove facility"
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
    ),
  });

  const columns: GridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Facility Name",
        flex: 1.5,
        renderCell: (params) => {
          const href = tenantId ? buildWasteFacilityUrl(tenantId, params.row.id) : null;
          if (href) {
            return (
              <MuiLink
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                noWrap
                title={params.row.name}
                onClick={(e) => e.stopPropagation()}
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {params.row.name || ""}
              </MuiLink>
            );
          }
          return (
            <TruncateDataGridCell lines={1} showTooltip variant="body2">
              {params.row.name || ""}
            </TruncateDataGridCell>
          );
        },
      },
      {
        field: "customerFacilityId",
        headerName: "ID",
        width: 70,
        renderCell: (params) => (
          <TruncateDataGridCell lines={1} showTooltip variant="body2">
            {params.row.customerFacilityId || ""}
          </TruncateDataGridCell>
        ),
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        width: 120,
        renderCell: (params) =>
          params.row.epaId ? (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <CopyableTypography text={params.row.epaId} variant="body2" />
            </Box>
          ) : null,
      },
      {
        field: "address",
        headerName: "Address",
        flex: 1.5,
        renderCell: (params) => {
          const { streetAddress1, city, state, zip } = params.row;
          const parts = [streetAddress1, [city, state].filter(Boolean).join(", "), zip].filter(
            Boolean
          );
          const fullAddress = parts.length > 0 ? parts.join(", ") : "";
          return (
            <TruncateDataGridCell lines={1} showTooltip variant="body2">
              {fullAddress}
            </TruncateDataGridCell>
          );
        },
      },
      ...(onRemoveFacility ? [getActionsColumn()] : []),
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [onRemoveFacility, tenantId]
  );

  // If no facilities and no add callback, don't render anything
  if (facilityIds.length === 0 && !onAddFacility) {
    return null;
  }

  if (!onAddFacility) {
    return (
      <>
        {facilityIssues.length > 0 && (
          <Alert severity="error">
            {facilityIssues.map((issue) => (
              <div key={issue.key}>{issue.message}</div>
            ))}
          </Alert>
        )}
        <SubmissionFormDatagrid rows={rows || []} columns={columns} loading={loading} />
      </>
    );
  }

  return (
    <>
      {facilityIssues.length > 0 && (
        <Alert severity="error">
          {facilityIssues.map((issue) => (
            <div key={issue.key}>{issue.message}</div>
          ))}
        </Alert>
      )}
      <DataGridWithAddButton
        rows={rows || []}
        columns={columns}
        addButtonConfig={{
          buttonText: "Add Facility",
          icon: <AddLocationAltOutlined />,
          colSpan: 4,
        }}
        onAdd={onAddFacility}
        createEmptyRow={() => ({
          id: "empty",
          name: "",
          customerFacilityId: null,
          streetAddress1: null,
          city: null,
          state: null,
          zip: null,
          epaId: null,
        })}
        loading={loading}
      />
    </>
  );
}
